import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import InvoiceForm from '../common/InvoiceForm';
import PrintPreview from '../common/PrintPreview';
import { Plus, Edit2, Trash2, Printer } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

export default function SalesOrder() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleSave = (data) => {
    if (editData) {
      dispatch({ type: 'DELETE_SALES_ORDER', payload: editData.id });
      dispatch({ type: 'ADD_SALES_ORDER', payload: data });
      addToast('Sales order updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_SALES_ORDER', payload: data });
      addToast('Sales order created successfully', 'success');
    }
    closeForm();
  };

  const handleEdit = (row) => { setEditData(row); setShowForm(true); };
  const handleDelete = (row) => {
    if (window.confirm(`Delete sales order ${row.orderNo}?`)) {
      dispatch({ type: 'DELETE_SALES_ORDER', payload: row.id });
    }
  };
  const closeForm = () => { setShowForm(false); setEditData(null); };

  if (printData) return <PrintPreview type="salesOrder" data={printData} onClose={() => setPrintData(null)} />;

  const columns = [
    { key: 'orderNo', label: 'Order No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'partyId', label: 'Customer', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'grandTotal', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'status', label: 'Status', render: (v) => (
      <span className={`badge ${v === 'Completed' ? 'badge-success' : v === 'Cancelled' ? 'badge-danger' : 'badge-warning'}`}>{v}</span>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Sales Orders</h1>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Order
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.salesOrders].reverse()}
          searchFields={['orderNo']}
          emptyState={{ title: 'No sales orders yet', description: 'Create your first sales order.', actionLabel: 'New Order', onAction: () => setShowForm(true) }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Order ${editData.orderNo}` : 'New Sales Order'} size="xl">
        <InvoiceForm
          key={editData ? editData.id : 'new'}
          type="salesOrder"
          onSave={handleSave}
          onCancel={closeForm}
          initialData={editData || undefined}
          nextEntryNo={editData ? editData.orderNo : `SO-${String(state.counters.salesOrder + 1).padStart(4, '0')}`}
        />
      </Modal>
    </div>
  );
}
